import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setRefreshSidebar } from "../../redux/sidebarSlice";
import { setSessionId as setReduxSessionId } from "../../redux/sessionSlice";
import api from "../../api";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
// import "../../css/EnterpriseDetails.css";
import "../../css/DocumentHistoryResponsive.css";
import { getFilteredDocs } from "../../utils/approvedContentTabs";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../../utils/toastMessages";
import { formatFileType } from "../../utils/FileType";

// Icons
import viewIcon from "../../assets/UserIcons/ViewIcon.svg";
import folderIcon from "../../assets/Images/folder.png";
import downloadIcon from "../../assets/Images/Download.png";
import filterIcon from "../../assets/Images/Filter.png";
import changeStatusIcon from "../../assets/icons/changeStatusIcon.png";
import deleteIcon from "../../assets/UserIcons/DeleteIcon.svg";
import trainDoc from "../../assets/UserIcons/FileIcon.svg";
import trainText from "../../assets/icons/TrainText.png";
import trainUrls from "../../assets/UserIcons/Website.svg";
import trainVideo from "../../assets/UserIcons/VideoIcon.svg";
import trainQA from "../../assets/icons/TrainQA.png";
import searchIcon from "../../assets/UserIcons/Search.svg";
import { formatDateToUserTimeZone } from "../../utils/formatDate";
import { useTableSort } from "../../hooks/useTableSort";
import { SortableHeader } from "../../components/SortableHeader";

const DocumentHistory = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const [allApprovedDocs, setAllApprovedDocs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = location.pathname;
  const dispatch = useDispatch();
  const { selectedModel, selectedMode } = useSelector((state) => state.model);

  const outlet = useOutletContext() || {};
  // Keep KB selections local on Document History. MainLayout clears global selections
  // on non-`/user/new` pages, and we transfer the final selection via sessionStorage on redirect.
  const [selectedKbFiles, setSelectedKbFiles] = useState([]);
  const selectedGroups = [];

  const normalizeFilename = (value) => String(value ?? "").trim();

  const tabs = [
    { name: "Documents", path: "/user/knowledge-repository" },
    { name: "Web URLs", path: "/user/knowledgerepository-weburls" },
    { name: "Video URLs", path: "/user/knowledgerepository-videourls" },
    // { name: "Deleted", path: "/user/knowledgerepository-deletedocs" },
    // { name: "Text", path: "/user/texthistory" },
    // { name: "Q&A", path: "/user/qahistory" },
  ];

  const [stats, setStats] = useState({
    user_files_active_count: 0,
    user_files_inactive_count: 0,
    uploaded_files_active_count: 0,
    uploaded_files_inactive_count: 0,
  });

  // ✅ Fetch stats
  const fetchStats = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("access_token");

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/files/stats`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (response.ok) {
        setStats(data.data || {});
      } else {
        // showErrorToast(data.message || "Failed to load document stats");
      }
    } catch (error) {
      console.error("❌ Error fetching stats:", error);
      showErrorToast("Something went wrong while fetching stats");
    } finally {
      setIsLoading(false);
    }
  };

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const fetchGlobalUserDocs = async () => {
    try {
      const res = await api.get(
        `${process.env.REACT_APP_API_URL}/uploaded-files/global-user-docs`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setAllApprovedDocs(res.data);
      // console.log(res.data);
    } catch (error) {
      console.error("❌ Error fetching approved docs:", error);
      // showErrorToast(TOAST_MESSAGES.DOCUMENT_HISTORY.FETCH_FAILED);
    }
  };

  useEffect(() => {
    fetchGlobalUserDocs();
    fetchStats();
  }, []);

  const toggleKbDoc = (file) => {
    if (!setSelectedKbFiles) return;
    const target = normalizeFilename(file?.name);
    if (!target) return;
    const rawDocId = file?.document_id ?? file?.documentId ?? null;
    const document_id =
      rawDocId == null || String(rawDocId).trim() === "" ? null : String(rawDocId);
    setSelectedKbFiles((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      const getName = (v) =>
        typeof v === "string" ? v : v && typeof v === "object" ? v.filename : "";
      const getId = (v) =>
        v && typeof v === "object" ? v.document_id ?? null : null;
      const key = (v) => normalizeFilename(v).toLowerCase();
      const targetKey = key(target);
      const map = new Map();
      for (const v of prevArr) {
        const name = normalizeFilename(getName(v));
        const k = key(name);
        if (!k) continue;
        if (!map.has(k)) {
          const existingId = getId(v);
          map.set(k, {
            filename: normalizeFilename(name),
            document_id:
              existingId == null || String(existingId).trim() === ""
                ? null
                : String(existingId),
          });
        }
      }
      if (map.has(targetKey)) map.delete(targetKey);
      else {
        const existing = map.get(targetKey) || null;
        map.set(targetKey, {
          filename: target,
          document_id: document_id ?? existing?.document_id ?? null,
        });
      }
      return Array.from(map.values());
    });
  };

  const handleKbNewChat = async () => {
    if (!selectedModel) {
      showErrorToast("Select a model first");
      return;
    }

    const knowledgeEnabled = Boolean(selectedKbFiles.length > 0);

    try {
      const res = await api.post(
        `${process.env.REACT_APP_API_URL}/chat/new-session`,
        {
          model_id: selectedModel,
          mode: selectedMode,
          knowledge_search_enabled: knowledgeEnabled,
          group_ids: selectedGroups,
          knowledge_base_files: selectedKbFiles,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const newSessionId = res.data?.session_id;
      if (!newSessionId) throw new Error("Missing session_id");

      try {
        sessionStorage.setItem(
          "kb_pending_selected_files",
          JSON.stringify(selectedKbFiles || []),
        );
        sessionStorage.setItem("kb_pending_selected_groups", JSON.stringify([]));
        sessionStorage.setItem("kb_auto_open_dropdown", "1");
        sessionStorage.setItem("kb_keep_selection_for_next_session", "1");
      } catch {}

      dispatch(setReduxSessionId(newSessionId));
      dispatch(setRefreshSidebar(true));

      navigate("/user/new", {
        state: { sessionId: newSessionId },
        replace: true,
      });
    } catch (err) {
      console.error("Failed to create session:", err);
      showErrorToast("Failed to create session");
    }
  };

  const handleDownload = async (pythonFileUUID) => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/uploaded-files/download`,
        {
          params: { python_file_uuid: pythonFileUUID },
          responseType: "blob", // required for file download
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const blob = response.data;

      // ✅ Get filename from Content-Disposition header
      let filename = "downloaded_file";
      const contentDisposition = response.headers["content-disposition"];

      if (contentDisposition) {
        const match = contentDisposition.match(
          /filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/i,
        );
        if (match && match[1]) filename = decodeURIComponent(match[1]);
      }

      // ✅ Trigger download (unchanged)
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      showSuccessToast("File downloaded successfully!");
    } catch (error) {
      console.error("❌ Download failed:", error);
      showErrorToast("Unable to download the file. Please try again.");
    }
  };

  // Delete function
  const handleDeleteDocument = async (fileId) => {
    // if (!window.confirm("Are you sure you want to delete this document?")) return;

    try {
      await api.patch(
        `${process.env.REACT_APP_API_URL}/uploaded-files/docs/soft-delete/${fileId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      showSuccessToast("Document deleted successfully");
      // Refresh data after delete
      fetchGlobalUserDocs();
    } catch (error) {
      console.error("❌ Error deleting document:", error);
      showErrorToast("Failed to delete document");
    }
  };

  const documentFiles = getFilteredDocs(allApprovedDocs, "document");

  const filteredFiles = documentFiles
    .filter((file) =>
      file.name?.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .filter((file) => {
      if (!dayFilter) return true;
      const updatedDate = new Date(file.updatedAt);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dayFilter === "Today") return updatedDate >= today;
      if (dayFilter === "Yesterday") {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return updatedDate >= yesterday && updatedDate < today;
      }
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.updatedAt);
      const dateB = new Date(b.updatedAt);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

  const totalEntries = filteredFiles.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filteredFiles.slice(indexOfFirst, indexOfLast);
  const showingFrom = totalEntries === 0 ? 0 : indexOfFirst + 1;
  const showingTo = Math.min(indexOfLast, totalEntries);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const handleReset = () => {
    setSortOrder("desc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const statsData = [
    {
      label: "Uploaded Documents",
      value: getFilteredDocs(allApprovedDocs, "document").length,
      icon: trainDoc,
      iconClass: "train-doc-icon",
    },
    {
      label: "Uploaded Web URLs",
      value: getFilteredDocs(allApprovedDocs, "webpage").length,
      icon: trainUrls,
    },
    // {
    //   label: "Uploaded Text",
    //   value: allApprovedDocs.filter((doc) => doc.type === "text").length,
    //   icon: trainText,
    // },
    {
      label: "Uploaded Video URLs",
      value: allApprovedDocs.filter((doc) => doc.type === "youtube_audio")
        .length,
      icon: trainVideo,
    },
    // {
    //   label: "Uploaded Q&A",
    //   value: allApprovedDocs.filter((doc) => doc.type === "qa").length,
    //   icon: trainQA,
    // },
    // {
    //   label: "Deleted Documents",
    //   value: stats.uploaded_files_inactive_count || 0,
    //   icon: deleteIcon,
    // },
  ];

  // View (Open) File using Google Docs Viewer (opens Excel/Word/PPT online)
  // const handleViewFile = (python_file_uuid) => {
  //   const fileUrl = `${process.env.REACT_APP_API_URL}/uploaded-files/view?python_file_uuid=${python_file_uuid}`;

  //   const googleViewUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
  //     fileUrl
  //   )}&embedded=true`;

  //   window.open(googleViewUrl, "_blank");
  // };

  const handleViewFile = (pythonFileUuid, originalFileName = "") => {
    const fileUrl = `${process.env.REACT_APP_API_URL}/uploaded-files/view?python_file_uuid=${pythonFileUuid}`;

    // console.log("file url for the file", fileUrl);

    // ✅ Detect image by extension
    const imageExtensions = ["png", "jpg", "jpeg", "webp", "gif"];
    const extension = originalFileName.split(".").pop()?.toLowerCase();

    // console.log("Detected extension:", extension);

    if (imageExtensions.includes(extension)) {
      // console.log("🖼 Opening image directly");
      window.open(fileUrl, "_blank");
      return;
    }

    // console.log("📄 Opening via Google Docs Viewer");
    const googleViewUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
      fileUrl,
    )}&embedded=true`;

    window.open(googleViewUrl, "_blank");
  };

  const { sortedData, requestSort, sortConfig } = useTableSort(currentData);

  const selectedKbFilesSet = useMemo(() => {
    return new Set(
      selectedKbFiles
        .map((v) =>
          normalizeFilename(
            typeof v === "string"
              ? v
              : v && typeof v === "object"
                ? v.filename
                : "",
          ).toLowerCase(),
        )
        .filter(Boolean),
    );
  }, [selectedKbFiles]);

  const visibleKbFilenames = useMemo(() => {
    return sortedData
      .map((file) => normalizeFilename(file?.name).toLowerCase())
      .filter(Boolean);
  }, [sortedData]);

  const visibleKbMetaByLowerName = useMemo(() => {
    const map = new Map();
    (sortedData || []).forEach((file) => {
      const filename = normalizeFilename(file?.name);
      const lower = filename.toLowerCase();
      if (!lower) return;
      const rawDocId = file?.document_id ?? file?.documentId ?? null;
      const document_id =
        rawDocId == null || String(rawDocId).trim() === "" ? null : String(rawDocId);
      map.set(lower, { filename, document_id });
    });
    return map;
  }, [sortedData]);

  const allVisibleSelected =
    visibleKbFilenames.length > 0 &&
    visibleKbFilenames.every((name) => selectedKbFilesSet.has(name));
  const someVisibleSelected = visibleKbFilenames.some((name) =>
    selectedKbFilesSet.has(name),
  );

  const selectAllRef = useRef(null);

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate =
      someVisibleSelected && !allVisibleSelected;
  }, [someVisibleSelected, allVisibleSelected]);

  const toggleSelectAllVisible = () => {
    setSelectedKbFiles((prev) => {
      const prevMetaByLower = new Map();
      (prev || []).forEach((v) => {
        const filename = normalizeFilename(
          typeof v === "string"
            ? v
            : v && typeof v === "object"
              ? v.filename
              : "",
        );
        const lower = filename.toLowerCase();
        if (!lower) return;
        const rawDocId = v && typeof v === "object" ? v.document_id ?? null : null;
        const document_id =
          rawDocId == null || String(rawDocId).trim() === "" ? null : String(rawDocId);
        prevMetaByLower.set(lower, { filename, document_id });
      });

      const set = new Set(
        (prev || [])
          .map((v) =>
            normalizeFilename(
              typeof v === "string"
                ? v
                : v && typeof v === "object"
                  ? v.filename
                  : "",
            ).toLowerCase(),
          )
          .filter(Boolean),
      );

      if (allVisibleSelected) {
        visibleKbFilenames.forEach((name) => set.delete(name));
      } else {
        visibleKbFilenames.forEach((name) => set.add(name));
      }

      return Array.from(set).map((lower) => {
        const meta =
          visibleKbMetaByLowerName.get(lower) ||
          prevMetaByLower.get(lower) ||
          null;
        return meta || { filename: lower, document_id: null };
      });
    });
  };

  const handleChangeFileStatus = async (python_file_uuid) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/uploaded-files/update_file_status/${python_file_uuid}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          // ❌ No body sent → Backend toggles automatically
        },
      );

      const result = await response.json();

      if (!response.ok) {
        showErrorToast(result.message || "Failed to update file status");
        return;
      }

      showSuccessToast("Status updated successfully");

      // refresh file list
      fetchGlobalUserDocs();
    } catch (err) {
      console.error("Error updating file status:", err);
      showErrorToast("Something went wrong");
    }
  };

  return (
    <div className="mx-2 pt-1 h-100">
      <div className="d-flex flex-wrap gap-3 mb-0 align-items-stretch">
        {statsData.map((item, index) => (
          <div
            key={index}
            className="dashboard-box p-3 rounded shadow-sm d-flex flex-column justify-content-between"
          >
            <div className="d-flex align-items-center mb-2">
              {item.icon && (
                <img
                  // className="icon-image"
                  className={`icon-image ${item.iconClass || ""}`}
                  src={item.icon}
                  alt={item.label}
                  style={{
                    width: "18px",
                    height: "18px",
                  }}
                />
              )}
              <h6 className="ms-2 mb-0 ak-text-md ">{item.label}</h6>
            </div>

            <h2 className="fw-bold ak-text-md">
              {typeof item.value !== "undefined" ? item.value : "0"}
            </h2>

            <div className="box-summary">
              {item.summary && item.summary.length > 0 ? (
                item.summary.map((s, i) => (
                  <span key={i}>
                    {s.label}: {s.value}
                  </span>
                ))
              ) : (
                <span className="invisible">placeholder</span> // Keeps space but invisible
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex refresh-prompts justify-content-between align-items-center mb-2 mt-1 ">
        <span className="fw-bold ak-text-lg">Uploaded Documents</span>
      </div>

      {/* <div className="mb-3 mt-3 tabs-wrapper">
        <div className="d-flex p-2 rounded border tab-container">
          {tabs.map((tab) => (
            <button
              key={tab.name}
              className={`tab-button ${
                currentTab === tab.path ? "active" : ""
              }`}
              onClick={() => navigate(tab.path)}
            >
              {tab.name}
            </button>
          ))}
        </div>
      </div> */}
      <div className="mt-3 tabs-wrapper">
        <div className="tab-container">
          {tabs.map((tab) => (
            <button
              key={tab.name}
              style={{
                fontWeight: currentTab === tab.path ? "700" : "500", // inline desktop logic
              }}
              className={`tab-button ak-text-md ${
                currentTab === tab.path ? "active" : ""
              }`}
              onClick={() => navigate(tab.path)}
            >
              {tab.name}
            </button>
          ))}
        </div>
      </div>
      {documentFiles?.length === 0 ? (
        <></>
      ) : (
        <div
          style={{ borderLeft: "1px solid var(--dark-color) " }}
          className="d-flex px-2 py-1 justify-content-between align-items-center "
        >
          <div className="d-flex align-items-center">
            <select
              className="form-select w-auto"
              value={entriesPerPage}
              onChange={handleEntriesChange}
            >
              {[50, 100, 500].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <label className="ms-2 mt-2 ak-text-md ">Entries per page</label>
          </div>

          {/* Search input */}
          <div className="search">
            <div className="kb-doc-search-actions">
              <div
                className="input-group custom-search-group"
                style={{ width: "320px" }}
              >
                <span className="input-group-text">
                  {/* <i className="bi bi-search"></i> */}
                  <img className="icon-image" src={searchIcon} alt="Search" />
                </span>
                <input
                  type="text"
                  className="form-control ak-text-md "
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <button
                type="button"
                className="btn-outline-secondary kb-doc-newchat-btn custom-btn"
                title="New Chat with selected files"
                disabled={selectedKbFiles.length === 0}
                onClick={handleKbNewChat}
              >
                <i className="bi bi-plus"></i>
                <span className="ms-1">New Chat</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="text-muted  profile-i ak-text-md ">
            {/* There are no document(s) in this section. */}
            No documents have been trained yet.
          </p>
        </div>
      ) : (
        <div
          className="not-disable"
          style={{ borderLeft: "1px solid var(--dark-color) " }}
        >
          <div className="table-responsive ak-text-md ">
            <table className="table DocHistory-table table-sm w-100">
              <thead className="text-left">
                <tr>
                  <th style={{ width: "34px" }} className="text-center">
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      className="doc-kb-checkbox"
                      checked={allVisibleSelected}
                      disabled={
                        !setSelectedKbFiles || visibleKbFilenames.length === 0
                      }
                      onChange={toggleSelectAllVisible}
                      title={allVisibleSelected ? "Deselect all" : "Select all"}
                      aria-label={
                        allVisibleSelected
                          ? "Deselect all documents"
                          : "Select all documents"
                      }
                    />
                  </th>
                  <SortableHeader
                    column="name"
                    label="Name"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="file_extension"
                    label="Type"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="size"
                    label="Size"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="approval_status"
                    label="Document Status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="mode"
                    label="Mode"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  {/* <SortableHeader
                    column="uploaded_by"
                    label="Uploaded By"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  /> */}
                  <SortableHeader
                    column="file_status"
                    label="Status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="createdAt"
                    label="Uploaded On"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="updatedAt"
                    label="Last Updated On"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                    className="text-center"
                  />
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((file, index) => (
                  <tr key={file.id || index}>
                    <td data-label="Select" className="text-center">
                      <input
                        type="checkbox"
                        className="doc-kb-checkbox"
                        checked={selectedKbFilesSet.has(
                          normalizeFilename(file.name).toLowerCase(),
                        )}
                        onChange={() => toggleKbDoc(file)}
                      />
                    </td>
                    <td
                      data-label="Document Name"
                      className="text-truncate text-left"
                      style={{ maxWidth: "180px" }}
                      title={file.name}
                    >
                      {file.name.replace(/\.[^/.]+$/, "")}
                    </td>

                    <td data-label="Document Type" className="text-left">
                      {formatFileType(file.type, file.name)}
                    </td>
                    <td className="text-left" data-label="Document Size">
                      {formatFileSize(file.size)}
                    </td>

                    <td className="text-left" data-label="Document Status">
                      <span
                        className={`badge ${
                          file.approval_status === "approved"
                            ? "bg-success"
                            : file.approval_status === "duplicate"
                              ? "bg-secondary"
                              : file.approval_status === "pending"
                                ? "bg-warning"
                                : "bg-danger"
                        }`}
                      >
                        {file.approval_status.charAt(0).toUpperCase() +
                          file.approval_status.slice(1)}
                      </span>
                    </td>

                    <td className="text-left" data-label="Mode">
                      <span
                        className="px-2 py-1 text-sm rounded-md text-[var(--primary-color)]"
                        style={{ display: "inline-block" }}
                      >
                        {(file.mode || "Personal").charAt(0).toUpperCase() +
                          (file.mode || "Personal").slice(1)}
                      </span>
                    </td>

                    <td className="text-left">
                      <span
                        className={`badge ${
                          file.file_status === "1"
                            ? "bg-success" // GREEN
                            : "bg-warning" // YELLOW
                        }`}
                      >
                        {file.file_status === "1" ? "Active" : "Inactive"}
                      </span>
                    </td>

                    <td className="text-left" data-label="Updated At">
                      {/* {new Date(file.createdAt).toLocaleString()} */}
                      {formatDateToUserTimeZone(file.createdAt)}
                    </td>
                    <td className="text-center" data-label="Updated At">
                      {/* {new Date(file.updatedAt).toLocaleString()} */}
                      {formatDateToUserTimeZone(file.updatedAt)}
                    </td>
                    <td className="text-center">
                      {/* <button
                        className="icon-button"
                        onClick={() =>
                          handleViewFile(file.python_file_uuid, file.name)
                        }
                        title="View"
                      >
                        <img src={viewIcon} className="icon-image" alt="View" />
                      </button> */}

                      <button
                        className="icon-button"
                        onClick={() => handleDownload(file.python_file_uuid)}
                        title="Download File"
                      >
                        <img
                          className="icon-image"
                          src={downloadIcon}
                          alt="Download File"
                        />
                      </button>
                      {/* <button
                        onClick={() =>
                          handleDeleteDocument(file.python_file_uuid)
                        }
                        className="icon-button"
                        title="Delete"
                        disabled={
                          file.approval_status === "approved" ||
                          file.approval_status === "rejected"
                        }
                      >
                        <img
                          className="icon-image"
                          src={deleteIcon}
                          alt="Delete"
                        />
                      </button> */}

                      {/* <button
                        onClick={() =>
                          handleChangeFileStatus(file.python_file_uuid)
                        }
                        className="icon-button"
                        title="Change Status"
                      >
                        <img
                          src={changeStatusIcon}
                          className="icon-image"
                          alt="Change Status"
                        />
                      </button> */}
                      <button
                        onClick={() =>
                          file.mode?.toLowerCase() !== "global" ||
                          (file.approval_status === "duplicate" &&
                            handleChangeFileStatus(file.python_file_uuid))
                        }
                        className={`icon-button ${
                          file.mode?.toLowerCase() === "global" ||
                          file.approval_status === "duplicate"
                            ? "disabled-icon"
                            : ""
                        }`}
                        title={
                          file.mode?.toLowerCase() === "global" ||
                          file.approval_status === "duplicate"
                            ? "Status change disabled for Global mode"
                            : "Change Status"
                        }
                        disabled={
                          file.mode?.toLowerCase() === "global" ||
                          file.approval_status === "duplicate"
                        }
                      >
                        <img
                          src={changeStatusIcon}
                          className="icon-image"
                          alt="Change Status"
                          style={{
                            opacity:
                              file.mode?.toLowerCase() === "global" ||
                              file.approval_status === "duplicate"
                                ? 0.4
                                : 1,
                            cursor:
                              file.mode?.toLowerCase() === "global" ||
                              file.approval_status === "duplicate"
                                ? "not-allowed"
                                : "pointer",
                          }}
                        />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table-footer bgcolor ak-text-md  fixed-bottom-footer d-flex justify-content-between align-items-center">
            <div className="refresh-prompts">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(1)}
                >
                  <i className="bi bi-chevron-double-left" />
                </button>
              </li>
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage - 1)}
                >
                  <i className="bi bi-chevron-left" />
                </button>
              </li>
              {pageNumbers.map((num) => (
                <li
                  key={num}
                  className={`page-item ${currentPage === num ? "active" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(num)}
                  >
                    {num}
                  </button>
                </li>
              ))}
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage + 1)}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </li>
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(totalPages)}
                >
                  <i className="bi bi-chevron-double-right" />
                </button>
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentHistory;
